import { Avatar, Card, CardContent, createStyles, Theme, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { AppImages } from "assets/appResources";
import { GlobalClassKeys } from "assets/jss/common-styles";
import CardButtonFilter from "components/Buttons/CardButtonFilter";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import IconButtonWeb from "components/Buttons/IconButtonWeb";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { GroupItemChangedDto, MemberShortViewModel, MemberSuggestionDto, PagedResult } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import MemberAutoComplete from "views/Member/Components/MemberAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 5;

enum FormMode { Search, Add }
enum GridMode { Normal, Expanded }

const styles = (theme: Theme) => createStyles({
    active: {
        color: theme.genie.palette.warning.main
    }
});


interface IProps {
    groupId: string;
    membersChanged: GroupItemChangedDto[];
    onChange: (data: GroupItemChangedDto[]) => void;
    onEndEdit?: () => void;
    readonly?: boolean;
}

interface IState {
    mode: FormMode,
    mouseOver: number,
    grid: GridMode,
    list: PagedResult<MemberShortViewModel>,
    searchText: string,
    offset: number;
    lastError: any;
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class GroupEditMember extends React.Component<Props, IState> {

    private searchRef: any = null;
    private addRef: any = null;

    ///
    static defaultProps = {
        readonly: false
    };

    ///
    constructor(props: Props) {
        super(props);

        this.state = {
            grid: GridMode.Normal,
            mode: props.readonly ? FormMode.Search : FormMode.Add,
            mouseOver: null,
            list: null,
            lastError: null,
            searchText: "",
            offset: 0,

        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Group.Components.GroupEditMember." + r, f);
    }

    ///
    private handleFilterSwitch = () => {
        this.setState({
            mode: this.state.mode == FormMode.Add ? FormMode.Search : FormMode.Add,
            searchText: ""
        }, () => {
            if (this.state.mode == FormMode.Search && this.searchRef) this.searchRef.focus();
            else if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
        });
    };

    ///
    private handleExitAddMode = () => {
        if (this.props.onEndEdit) this.props.onEndEdit();
    };

    ///
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.currentTarget.value });
    };

    ///
    private handleAdd(item: MemberSuggestionDto) {
        var members = this.props.membersChanged.slice(0);

        const m = members.find(p => p.id == item.key);
        if (m == null) {
            api.checkCanAddMemberIntoGroup(this.props.groupId, item.key)
                .then((result) => {

                    if (result == "") {
                        // Não existe previamente.
                        members.push({
                            id: item.key,
                            operation: "add"
                        });

                        this.props.onChange(members);
                    }
                    else if (result == "CannotAddItSelf")
                        this.props.snackbar.showMessage(this.res("CannotAddItSelfMessage"), "error");
                    else if (result == "AlreadyExists")
                        this.props.snackbar.showMessage(this.res("UserOrMemberAlreadyExistsMessage"), "error");
                    else if (result == "CircularReference")
                        this.props.snackbar.showMessage(this.res("CircularReferenceMessage"), "warning");
                });
        }
        else if (m.operation == "delete") {
            members = members.filter(p => p.id != item.key);
            this.props.onChange(members);
        }
        else
            this.props.snackbar.showMessage(this.res("AlreadyExistsMessage"), "error");
    }

    ///
    private handleDelete(item: MemberShortViewModel) {
        var members = this.props.membersChanged.slice(0);

        const m = members.find(p => p.id == item.memberId);
        if (m == null)
            members.push({ id: item.memberId, operation: "delete" });
        else if (m.operation == "add")
            members = members.filter(p => p.id != item.memberId);

        this.props.onChange(members);
    }

    ///
    private handleGridMode = () => {
        this.setState(
            {
                grid: this.state.grid == GridMode.Normal ? GridMode.Expanded : GridMode.Normal,
                list: null,
                offset: 0,
                searchText: ""
            }, () => this.loadList(0)
        );
    };

    ///
    private loadList(offset: number) {

        this.setState({ list: null });

        const fn = (this.state.grid == GridMode.Normal)
            ? api.getSimulatedGroupMembers
            : api.getSimulatedGroupUsers;

        const newMembers = this.props.membersChanged.filter(p => p.operation == "add").map((v) => v.id);
        const delMembers = this.props.membersChanged.filter(p => p.operation == "delete").map((v) => v.id);

        fn(this.props.groupId, newMembers, delMembers, this.state.searchText, { offset, take: MaxTakeRecords })
            .then((result) => {
                this.setState({ list: result, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, offset: 0 });
            });
    }

    ///
    private handleFilterClear = () => {
        this.setState(
            {
                mode: this.props.readonly ? FormMode.Search : FormMode.Add,
                searchText: "",
                list: null
            },
            () => {
                this.loadList(0);
                if (this.state.mode == FormMode.Add && this.addRef) this.addRef.focus();
            }
        );
    };

    ///
    private renderItems() {
        const { list } = this.state;

        return list.items.map((g, index) => {
            return (
                <Grid
                    key={index}
                    container
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                    wrap="nowrap"
                    className={this.state.mouseOver == index ? GlobalClassKeys.overElementBackground : null}
                    onMouseEnter={() => this.setState({ mouseOver: index })}
                    onMouseLeave={() => this.setState({ mouseOver: null })}
                >
                    <Grid item>
                        <Avatar src={g.thumbnailUrl || AppImages.DefaultGroupThumbnail} />
                    </Grid>
                    <GridEx item xs zeroMinWidth alignSelf="center">
                        <Typography noWrap>
                            {g.name}
                        </Typography>
                    </GridEx>
                    {
                        this.state.grid == GridMode.Normal && !this.props.readonly &&
                        <Grid item>
                            <IconButtonDelete size="small" color="muted" onClick={() => this.handleDelete(g)} />
                        </Grid>
                    }
                </Grid>
            );
        });
    }

    ///
    private renderList() {
        const { list, searchText } = this.state;

        return (
            <div>
                <MuiContainer height={230}>
                    {
                        list == null
                            ? <SpinnerIndicator />
                            : list.total == 0 && searchText == ""
                                ? <Typography variant="subtitle1">{this.res("EmptyData")}</Typography>
                                : list.total == 0 && searchText != ""
                                    ? (
                                        <MuiMessageBox
                                            variant="warning"
                                            message={this.res("FilteredEmptyData")}
                                        />
                                    )
                                    : this.renderItems()
                    }
                </MuiContainer>
                <div style={{ height: 36 }}>
                    {
                        list != null && list.total > 0 &&
                        <MuiPagination
                            limit={MaxTakeRecords}
                            offset={this.state.offset}
                            total={this.state.list.total}
                            onClick={(e, offset) => this.loadList(offset)}
                        />
                    }
                </div>
            </div>
        );
    }

    ///
    public componentDidMount() {
        this.loadList(0);
    }

    ///
    public componentDidUpdate(prevProps: IProps) {
        if (prevProps.membersChanged != this.props.membersChanged) {
            this.loadList(0);
        }
    }

    ///
    public render() {
        const { lastError } = this.state;

        if (lastError)
            return <RequestErrorReport error={lastError} />;

        return (
            <Card>
                <CardHeaderColor color="primary"
                    title={
                        <>
                            <Grid container={true} justifyContent={"space-between"} >
                                {
                                    !this.props.readonly &&
                                    <Grid item>
                                        <CardButtonFilter
                                            iconSize="large"
                                            color={this.state.mode == FormMode.Add ? "inherit" : "warning"}
                                            title={this.res("ButtonAddTitle")}
                                            onClick={this.handleFilterSwitch}
                                        />
                                    </Grid>
                                }

                                <GridEx item xs alignSelf="center" className={GlobalClassKeys.textCenter}>
                                    <span>{this.res("HeaderTitle")}</span>
                                </GridEx>

                                {
                                    false &&
                                    <Grid>
                                        <IconButtonWeb
                                            color={this.state.grid == GridMode.Expanded ? "warning" : "inherit"}
                                            title={this.res("ExpandGroupTitle")}
                                            onClick={this.handleGridMode}
                                        />
                                    </Grid>
                                }
                            </Grid>

                            <Grid container={true} justifyContent={"space-between"} >
                                <GridEx xs>
                                    {
                                        this.state.mode == FormMode.Search
                                            ? <MuiTextFieldColor
                                                fullWidth
                                                placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                                                inputColor="warning"
                                                underlineColor="warning"
                                                value={this.state.searchText}
                                                onChange={this.handleFilterChange}
                                                onKeyDown={(e) => { if (e.keyCode == 13) this.loadList(0); }}
                                                endAdornment={
                                                    this.state.searchText == ""
                                                        ? null
                                                        : <IconButtonCancel color="warning" size="small" onClick={this.handleFilterClear} />
                                                }
                                                inputRef={(ref) => this.searchRef = ref}
                                            />
                                            : <MemberAutoComplete
                                                placeholder={this.res("MemberPlaceholder")}
                                                showUsersOnly
                                                escapeOnEmpty
                                                selectedItems={null}
                                                inputColor={"inherit"}
                                                underlineColor={"inherit"}
                                                onChange={(data) => this.handleAdd(data[0])}
                                                onEscape={this.handleExitAddMode}
                                                onBlur={this.handleExitAddMode}
                                                inputRef={(ref) => this.addRef = ref}
                                            />
                                    }
                                </GridEx>
                            </Grid>
                        </>
                    }
                >
                </CardHeaderColor>

                <CardContent>
                    {this.renderList()}
                </CardContent>

            </Card>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(GroupEditMember)));